import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { PlatformLayout } from "./platform-layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Download, Search, RefreshCw } from "lucide-react";
import { CustomersSpreadsheet } from "@/components/spreadsheet/customers-spreadsheet";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

export default function PlatformCustomersPage() {
  const [searchQuery, setSearchQuery] = useState("");
  const { toast } = useToast();
  const qc = useQueryClient();

  const backfillMutation = useMutation({
    mutationFn: () => apiRequest("POST", "/api/platform/customers/backfill", {}),
    onSuccess: async (res) => {
      const data = await res.json();
      qc.invalidateQueries({ queryKey: ["/api/platform/customers"] });
      toast({ title: data.message });
    },
    onError: () => toast({ title: "Backfill failed", variant: "destructive" }),
  });

  return (
    <PlatformLayout fullBleed>
      <div className="flex flex-col h-full" data-testid="page-platform-customers">
        <div className="px-6 py-4 border-b flex items-center justify-between gap-4 flex-wrap flex-shrink-0">
          <div>
            <h1 className="text-xl font-semibold">Customers</h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              Manage Clarik customer accounts. Click any cell to edit inline.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search customers…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8 h-9 w-56"
                data-testid="input-search-customers"
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => backfillMutation.mutate()}
              disabled={backfillMutation.isPending}
              title="Create customer records for any workspaces that don't have one yet"
              data-testid="button-backfill-customers"
            >
              <RefreshCw className={`w-4 h-4 mr-1.5 ${backfillMutation.isPending ? "animate-spin" : ""}`} />
              Sync Workspaces
            </Button>
            <Button variant="outline" size="sm" onClick={() => window.open("/api/platform/customers/export/csv", "_blank")} data-testid="button-export-customers">
              <Download className="w-4 h-4 mr-1.5" /> Export CSV
            </Button>
          </div>
        </div>

        <div className="flex-1 overflow-hidden">
          <CustomersSpreadsheet searchQuery={searchQuery} />
        </div>
      </div>
    </PlatformLayout>
  );
}
